// /events — every event across every group the viewer belongs to.
//
// The same two-list split as the per-group event box: what is coming up, then
// the archive. The difference is that a card here has to say WHICH group it
// belongs to, because two groups can both run a "Poker Night".
//
// One request, not one per group. See listEventsForUser() on the server for why
// the membership join there is the authorization for this whole view.

import { ApiClientError, listMyEvents } from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import type { MyEventSummary } from '@msc/shared';
import { viewerZone } from '../viewerZone.js';
import { eventCard } from './eventCard.js';
import { todayInZone } from './events.js';

export const allEventsView: View = async (outlet) => {
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  let events: MyEventSummary[];
  let zone: string;
  try {
    [events, zone] = await Promise.all([listMyEvents({ limit: 200 }), viewerZone()]);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load your events.'));
    return;
  }

  // Cut on the VIEWER's today, not the server's site-local one — same reasoning
  // as the group event box.
  const today = todayInZone(zone);
  const isUpcoming = (e: MyEventSummary) => e.closedAt === null && e.eventDate >= today;
  const box = events.filter(isUpcoming);
  const archive = events.filter((e) => !isUpcoming(e)).reverse();

  main.replaceChildren(
    el('div', { class: 'page-head' }, [
      el('h1', {}, 'All events'),
      el('p', { class: 'muted' }, `Times shown in ${zone}`),
    ]),
    el('nav', { class: 'subnav' }, [el('a', { href: '/calendar' }, 'Calendar view')]),
    el('h2', {}, 'Coming up'),
    box.length
      ? el(
          'div',
          { class: 'event-list' },
          box.map((e) =>
            eventCard(e, zone, {
              rsvp: true,
              groupLabel: { groupId: e.groupId, name: e.groupName },
            }),
          ),
        )
      : el('p', { class: 'muted' }, 'Nothing coming up in any of your groups.'),
    el('h2', {}, 'Past and closed'),
    archive.length
      ? el(
          'div',
          { class: 'event-list' },
          archive.map((e) =>
            eventCard(e, zone, { groupLabel: { groupId: e.groupId, name: e.groupName } }),
          ),
        )
      : el('p', { class: 'muted' }, 'Nothing here yet.'),
  );
};
